import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BellRing, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const JobAlertsCard = () => {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [location, setLocation] = useState('');
  const [radius, setRadius] = useState('50');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, alertRes] = await Promise.all([
          fetch(`${API}/jobs/categories`),
          fetch(`${API}/job-alerts`, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
        ]);
        if (catRes.ok) setCategories(await catRes.json());
        if (alertRes.ok) {
          const a = await alertRes.json();
          setEnabled(!!a.enabled);
          setLocation(a.location || '');
          setRadius(String(a.radius_km || 50));
          setSelected(a.categories || []);
        }
      } catch (e) {
        console.error('Job alerts load error:', e);
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [token]);

  const toggleCategory = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/job-alerts`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ enabled, location, radius_km: Number(radius), categories: selected }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(enabled ? 'Alerty o pracy włączone!' : 'Ustawienia zapisane');
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Nie udało się zapisać');
      }
    } catch (e) {
      toast.error('Wystąpił błąd');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="job-alerts-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <BellRing className="w-5 h-5 text-violet-600" />Alerty o pracy
        </CardTitle>
        <p className="text-sm text-slate-500">
          Dostaniesz maila, gdy w Twojej okolicy pojawi się nowa oferta w Giełdzie Pracy.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
              <div>
                <p className="font-medium text-slate-900">Powiadomienia e-mail</p>
                <p className="text-sm text-slate-500">Możesz je wyłączyć w każdej chwili</p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="job-alerts-toggle" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="alertLocation">Twoja miejscowość</Label>
                <Input
                  id="alertLocation"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="np. Kraków"
                  data-testid="job-alert-location-input"
                />
              </div>
              <div>
                <Label>Promień poszukiwań</Label>
                <Select value={radius} onValueChange={setRadius}>
                  <SelectTrigger data-testid="job-alert-radius-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">do 25 km</SelectItem>
                    <SelectItem value="50">do 50 km</SelectItem>
                    <SelectItem value="70">do 70 km</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Interesujące Cię zawody</Label>
              <p className="text-xs text-slate-500 mb-3">Nic nie zaznaczasz = wszystkie zawody</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <label key={cat.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <Checkbox
                      checked={selected.includes(cat.id)}
                      onCheckedChange={() => toggleCategory(cat.id)}
                      data-testid={`job-alert-cat-${cat.id}`}
                    />
                    {cat.name}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="save-job-alerts-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Zapisywanie...</> : 'Zapisz alerty'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobAlertsCard;
